'use strict';

import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {useSelector} from 'react-redux';
import _ from 'lodash';
import {keys} from 'ts-transformer-keys';

import {
    Password,
    PasswordProps
} from 'primereact/password';

import {__} from '~/shared/src/utilities/messageCatalog';
import {useHookFormDelay} from '~/shared/src/utilities/modules/form';

import FieldWrapper, {
    DefaultProps as FieldWrapperDefaultProps,
    defaultProps as fieldWrapperDefaultProps,
    Props as FieldWrapperProps,
    ComponentProps as FieldWrapperComponentProps,
    componentProps as fieldWrapperComponentProps
} from '~/frontend/src/components/base/shared/field/FieldWrapper';

import {StoreState} from '~/frontend/src/store';


type InputProps = Omit<PasswordProps, 'autoComplete' | 'onChange' | 'key'>;
const inputProps = keys<InputProps>();

type InputChangeProps = React.ChangeEvent<HTMLInputElement>;

type DefaultProps = FieldWrapperDefaultProps & {

    /**
     * Should the field marked if dirty.
     */
    markDirty? : boolean;

    /**
     * The CSS class names to style the input field with.
     */
    inputClassNames? : string[];

    /**
     * The styles to style the input field with.
     */
    inputStyle? : React.CSSProperties;

    /**
     * true to use react-hook-form with debounced onChange event
     */
    useHookForm? : boolean;
};
const defaultProps : Required<DefaultProps> = {
    ...fieldWrapperDefaultProps,

    markDirty      : false,
    inputClassNames: [],
    inputStyle     : {},
    useHookForm    : false
};

type Props = Omit<FieldWrapperProps, 'pt'> & {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The value.
     */
    value? : string;

    /**
     * The value to determine dirty against.
     */
    originalValue? : string;

    /**
     * Fired on change of input immediately if useHookForm is false, debounced if it's true
     * @param value
     * @param originalEvent
     */
    onChange : ( value : string | undefined, originalEvent : any ) => void;

    /**
     * Fired on change of input immediately if useHookForm is true otherwise never fired
     * @param value
     * @param originalEvent
     */
    onIntermediateChange? : ( value : string | undefined, originalEvent : any ) => void;

    pt? : {
        fieldWrapper? : FieldWrapperProps[ 'pt' ];
    };
};

export type ComponentProps = InputProps & DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = InputProps & Required<DefaultProps> & Props;


// outside the default values are not required (AllProps),
// but inside they are required (set to default) for static type checking (AllPropsWithDefaultsSet)
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const PasswordField = React.forwardRef<Password, ComponentProps>( ( props : AllProps, forwardedRef ) => {
    // avoid rendering if hidden
    if ( props.visibility === 'hidden' ) {
        return null;
    }
    else {
        useSelector( ( state : StoreState ) => state.session?.user );

        const {
                  uid,
                  id,
                  value,
                  originalValue,
                  appendToElementId,
                  inputClassNames,
                  inputStyle,
                  markDirty,
                  messagePlaceholders,
                  messageTemplateStringResolver,
                  onChange,
                  onIntermediateChange,
                  onFocus,
                  onBlur,
                  pt,
                  useHookForm
              }                                 = props,
              elementId                         = id ?? `${uid}_passwordField`,
              inValue                           = useRef<string | undefined>( value ),
              outValue                          = useRef<string | undefined>( value ),
              timeoutId                         = useRef<NodeJS.Timeout | undefined>(),
              [ elementValue, setElementValue ] = useState<string | undefined>( value ),
              [ focus, setFocus ]               = useState<boolean>( false ),
              [ appendTo, setAppendTo ]         = useState<'self' | HTMLElement>( 'self' ),
              messageResolver                   = useMemo(
                  () => {
                      if ( !messageTemplateStringResolver ) {
                          return ( messageKey : string ) : string | undefined => messageKey === '' ? undefined : __( messageKey, messagePlaceholders, false );
                      }
                      else {
                          return ( messageKey : string ) => messageKey === '' ? undefined : __( messageTemplateStringResolver( __( messageKey, messagePlaceholders ) ) ?? '', undefined, false );
                      }
                  },
                  [
                      JSON.stringify( messagePlaceholders ),
                      messageTemplateStringResolver
                  ]
              ),
              onInputChange                     = useCallback(
                  ( event : InputChangeProps ) => {
                      const changedValue = event.target.value;

                      if ( changedValue !== outValue.current ) {
                          setElementValue( changedValue );

                          onChange( changedValue, event );
                      }
                  },
                  [
                      onChange
                  ]
              ),
              onHookFormInputChange             = useCallback(
                  ( event : InputChangeProps ) => {
                      const changedValue = event.target.value;

                      if ( changedValue !== outValue.current ) {
                          setElementValue( changedValue );

                          onIntermediateChange?.( changedValue, event );

                          if ( timeoutId.current ) {
                              clearTimeout( timeoutId.current );
                          }
                          timeoutId.current = setTimeout( onChange.bind( null, changedValue, event ), useHookFormDelay );
                      }
                  },
                  [
                      onChange,
                      onIntermediateChange
                  ]
              ),
              onInputFocus                      = useCallback(
                  ( event : React.FocusEvent<HTMLInputElement> ) => {
                      setFocus( true );

                      onFocus?.( event );
                  },
                  [
                      onFocus
                  ]
              ),
              onInputBlur                       = useCallback(
                  ( event : React.FocusEvent<HTMLInputElement> ) => {
                      setFocus( false );

                      if ( timeoutId.current ) {
                          clearTimeout( timeoutId.current );
                      }

                      if ( outValue.current !== inValue.current ) {
                          onChange( outValue.current, event );
                      }

                      onBlur?.( event );
                  },
                  [
                      onBlur
                  ]
              );

        inValue.current = value;
        outValue.current = elementValue;

        useEffect(
            () => {
                if ( !focus ) {
                    setElementValue( value );
                }
            },
            [
                value
            ]
        );

        // resolve appendTo
        useLayoutEffect(
            () => {
                if ( appendToElementId && appendToElementId !== 'self' ) {
                    let appendToElement : HTMLElement | null;

                    if ( appendToElementId !== 'document' ) {
                        appendToElement = document.getElementById( appendToElementId );
                    }
                    else {
                        appendToElement = document.body;
                    }

                    if ( appendToElement && appendToElement !== appendTo ) {
                        setAppendTo( appendToElement );
                    }
                }
            }
        );

        return <FieldWrapper
            {..._.pick( props, fieldWrapperComponentProps ) as FieldWrapperComponentProps}

            uid={elementId}

            isDirty={markDirty && ( elementValue ?? '' ) !== ( originalValue ?? '' )}

            autoComplete={false}
            pt={pt?.fieldWrapper}
        >
            <form className="yn-flex-layout-parent">
                <Password
                    {..._.pick( props, inputProps ) as PasswordProps}

                    id={elementId}

                    ref={forwardedRef}

                    value={elementValue ? elementValue : ''}

                    onChange={useHookForm ? onHookFormInputChange : onInputChange}
                    onFocus={onInputFocus}
                    onBlur={onInputBlur}

                    feedback={false}
                    toggleMask={true}
                    className={`yn-passwordfield ${inputClassNames.join( ' ' )}`}
                    inputClassName={inputClassNames.join( ' ' )}
                    style={{ ...defaultProps.inputStyle, ...inputStyle }}

                    tooltip={undefined}
                    tooltipOptions={undefined}

                    aria-describedby={`${elementId}_message`}

                    autoComplete="off"

                    appendTo={appendTo}
                />
            </form>
        </FieldWrapper>;
    }
} );
PasswordField.defaultProps = defaultProps;
PasswordField.displayName = 'PasswordField';

export default PasswordField;
